/**
 * Checkbox Atom
 *
 * A checkbox input element for boolean selections.
 */

'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

interface GroupCheckboxProps {
  isGroupItem?: boolean;
  areAllCheckboxesInGroupChecked?: boolean;
}

const Checkbox = React.forwardRef<
  React.ComponentRef<typeof CheckboxPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & GroupCheckboxProps
>(({ className, isGroupItem = false, areAllCheckboxesInGroupChecked, ...props }, ref) => (
  <CheckboxPrimitive.Root
    ref={ref}
    className={cn(
      'peer h-5 w-5 shrink-0 border border-slate-900 ring-offset-white hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-slate-900 data-[state=checked]:text-slate-50',
      className
    )}
    {...props}>
    {!isGroupItem && (
      <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
        <Check className='h-5 w-5' />
      </CheckboxPrimitive.Indicator>
    )}
    {isGroupItem &&
      (areAllCheckboxesInGroupChecked ? (
        <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
          <Check className='h-5 w-5' />
        </CheckboxPrimitive.Indicator>
      ) : (
        <CheckboxPrimitive.Indicator className={cn('flex items-center justify-center text-current')}>
          <Minus className='h-5 w-5' />
        </CheckboxPrimitive.Indicator>
      ))}
  </CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };

export type CheckboxWithTextProps = {
  value?: string | number | readonly string[];
  checked?: CheckboxPrimitive.CheckedState;
  title: string | React.ReactNode;
  description?: string;
  onCheckedChange: () => void;
};

export function CheckboxWithText({ title, description, ...props }: CheckboxWithTextProps) {
  return (
    <div className='flex items-center space-x-2'>
      <Checkbox id='checkbox' {...props} />
      <div className=''>
        <label htmlFor='checkbox' className='text-sm peer-disabled:cursor-not-allowed peer-disabled:opacity-70'>
          {title}
        </label>
        {Boolean(description) && <p className='text-muted-foreground text-sm'>{description}</p>}
      </div>
    </div>
  );
}
